// Every live plan that covers the signed-in doctor (Wave 2b, §11.7a).
//
// Mirrors GET /api/account/provider/plan-rows
// (apps/api/src/routes/account-provider-plan-rows.ts), which projects the
// gate's own `resolveProviderRows`. One row = one card on the billing page:
// his own purchase, and one per clinic that spends a seat on him.

import { apiFetch } from "@pontimed/api-client";
import { useQuery } from "@tanstack/react-query";

import type { LocalizedString } from "@/types/localized-string";

export interface ProviderPlanRow {
  planCode: string;
  planName: LocalizedString | null;
  planLabel: string;
  /** Monthly price of the plan — the ladder the gate ranks rows by. */
  tier: number;
  origin: "own" | "coverage";
  /** Null for own rows AND for coverage whose parent is not a facility. */
  context: { facilityId: string; facilityName: string } | null;
  /** Parent entity type behind the row: 'facility' = real clinic coverage. */
  parentEntityType: string;
  payer: "you" | "facility";
  /** 'purchase' | 'founding' | 'admin' | 'referral'. */
  source: string;
  endsAt: string | null;
  currentPeriodEnd: string | null;
  status: string;
  price: number;
  currency: string;
  planInterval: string;
  isFounding: boolean;
  subscriptionId: string;
}

export const PROVIDER_PLAN_ROWS_KEY = ["api", "/api/account/provider/plan-rows"] as const;

/**
 * Rows for the provider workspace. `enabled` is false everywhere else, so the
 * user / facility / business billing pages make no extra request and behave
 * exactly as before.
 *
 * A caller without a provider profile gets 403 — treated as "no rows", not an
 * error state, so the page keeps rendering the payer's own subscriptions.
 */
export function useProviderPlanRows(enabled: boolean) {
  return useQuery({
    queryKey: PROVIDER_PLAN_ROWS_KEY,
    queryFn: () => apiFetch.get<{ rows: ProviderPlanRow[] }>("/api/account/provider/plan-rows"),
    enabled,
    retry: false,
    staleTime: 30_000,
  });
}
